import { SPRINT_SPEED_MULTIPLIER, type Direction } from '@veriverse/game-logic';
import {
  CHARACTER_FRAME,
  DEFAULT_APPEARANCE,
  parseAppearanceKey,
  TILE_SIZE,
  EMOTE_DURATION_MS,
  TYPING_BUBBLE_TTL_MS,
  type EmoteId,
  type CharacterId,
  type PlayerStatus,
} from '@veriverse/shared';
import type Phaser from 'phaser';
import { activityIcon } from './activity-icons.js';
import { partyHatFrame } from './birthday-art.js';
import {
  animationKey,
  ensureSeatedAnimation,
  firstFrame,
  SHEET_FRAME,
  SHEET_ROWS,
  SHEET_SCALE,
  seatedHeadDrop,
  sheetKey,
  type CharacterAnimation,
} from './characters.js';
import { characterDepth, decorDepth, speakingDepth } from './depth.js';
import { prefersReducedMotion } from '../state/settings.js';
import { emoteAnimationKey, emoteTextureKey, EMOTE_TAIL_ORIGIN } from './emotes.js';
import { puffDust } from './sprint-dust.js';

const BUBBLE_MS_BASE = 3_500;
const BUBBLE_MS_PER_CHAR = 45;
const BUBBLE_MS_MAX = 9_000;
const BUBBLE_MAX_CHARS = 140;
/** The typing bubble's dots fill up one by one (a still "..." with reduced motion). */
const TYPING_FRAMES = ['.  ', '.. ', '...'] as const;
const TYPING_FRAME_MS = 350;
/** Grace after a step before a walking character stands still, so held keys walk smoothly. */
const IDLE_AFTER_STEP_MS = 60;
/** The party cone sits this many source pixels higher than drawn, so the eyes stay visible. */
const HAT_LIFT = 2;
/** The name tag and speech bubble move up this far so the hat's tip shows, in screen pixels. */
const HAT_TAG_RAISE = 14;
const LABEL_Y = -CHARACTER_FRAME.height - 2;
/** The dust puff sits this far above the feet, in screen pixels, so it frames the heels. */
const DUST_LIFT = 4;

export interface AvatarInit {
  readonly name: string;
  readonly appearanceKey: string;
  readonly status: PlayerStatus;
  /** What the player is doing at an object (see `activityIcon`), or `''`. */
  readonly activity?: string;
  readonly tileX: number;
  readonly tileY: number;
  readonly isLocal: boolean;
  readonly facing?: Direction;
}

/**
 * One person on screen: sprite, name tag and speech bubble, moved tile by tile with a short tween.
 * The character (see `characters.ts`) walks and idles facing its direction. Offline colleagues
 * are taken to be working: they sit at their desk typing (seen from behind) with a dimmed name
 * tag. Away colleagues get a "zz" marker. On their birthday they wear a party hat.
 */
export class Avatar {
  readonly container: Phaser.GameObjects.Container;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly sprite: Phaser.GameObjects.Sprite;
  /** Party hat on the person's birthday (spec §6.13), following the body frame for frame. */
  private readonly hat: Phaser.GameObjects.Image;
  private readonly label: Phaser.GameObjects.Text;
  private readonly marker: Phaser.GameObjects.Text;
  private readonly activityMarker: Phaser.GameObjects.Text;
  private bubble: Phaser.GameObjects.Text | undefined;
  private bubbleTimer: Phaser.Time.TimerEvent | undefined;
  private typingBubble: Phaser.GameObjects.Text | undefined;
  private typingExpiry: Phaser.Time.TimerEvent | undefined;
  private typingDots: Phaser.Time.TimerEvent | undefined;
  private emoteSprite: Phaser.GameObjects.Sprite | undefined;
  private emoteTimer: Phaser.Time.TimerEvent | undefined;
  private tileX: number;
  private tileY: number;
  private tween: Phaser.Tweens.Tween | undefined;
  private currentStatus: PlayerStatus;
  private currentKey: string;
  private character: CharacterId | undefined;
  private facing: Direction;
  private walking = false;
  private sprinting = false;
  private idleTimer: Phaser.Time.TimerEvent | undefined;

  constructor(
    private readonly scene: Phaser.Scene,
    init: AvatarInit,
  ) {
    this.tileX = init.tileX;
    this.tileY = init.tileY;
    this.currentStatus = init.status;
    this.currentKey = init.appearanceKey;
    this.facing = init.facing ?? 'down';

    // Soft ground shadow at the feet, first in the container so it draws under the sprite.
    this.shadow = scene.add.ellipse(0, -2, 16, 6, 0x000000, 0.28);
    this.sprite = scene.add.sprite(0, 0, '__DEFAULT').setOrigin(0.5, 1);
    this.hat = scene.add.image(0, 0, '__DEFAULT').setOrigin(0.5, 1).setVisible(false);
    // Phaser's animation events, by name: this module only imports Phaser's types.
    this.sprite.on('animationstart', () => this.syncHat());
    this.sprite.on('animationupdate', () => this.syncHat());
    this.applyLook();
    this.label = scene.add
      .text(0, LABEL_Y, init.name, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: init.isLocal ? '#ffe9a8' : '#ffffff',
        backgroundColor: 'rgba(20, 22, 30, 0.7)',
        padding: { x: 2, y: 1 },
      })
      .setOrigin(0.5, 1)
      .setResolution(2);
    this.marker = scene.add
      .text(CHARACTER_FRAME.width / 2 - 4, -CHARACTER_FRAME.height + 6, 'zz', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#a3acbd',
      })
      .setOrigin(0.5, 1)
      .setResolution(2);

    this.activityMarker = scene.add
      .text(-CHARACTER_FRAME.width / 2 + 3, -CHARACTER_FRAME.height + 6, '', {
        fontFamily: 'sans-serif',
        fontSize: '9px',
      })
      .setOrigin(0.5, 1)
      .setResolution(2);

    const position = Avatar.toPixels(init.tileX, init.tileY);
    this.container = scene.add.container(position.x, position.y, [
      this.shadow,
      this.sprite,
      this.hat,
      this.label,
      this.marker,
      this.activityMarker,
    ]);
    this.setActivity(init.activity ?? '');
    this.applyStatus();
    this.updateDepth();
  }

  get tile(): { x: number; y: number } {
    return { x: this.tileX, y: this.tileY };
  }

  get status(): PlayerStatus {
    return this.currentStatus;
  }

  setName(name: string): void {
    if (this.label.text !== name) this.label.setText(name);
  }

  setAppearance(key: string): void {
    if (key === this.currentKey) return;
    this.currentKey = key;
    this.applyLook();
  }

  /** Turns on the spot (e.g. a key tapped against a wall, or the server's facing). */
  setFacing(direction: Direction): void {
    if (direction === this.facing) return;
    this.facing = direction;
    this.play(this.walking ? 'walk' : 'idle');
  }

  setStatus(status: PlayerStatus): void {
    if (status === this.currentStatus) return;
    this.currentStatus = status;
    this.applyStatus();
  }

  /** Shows the activity icon (e.g. "playing" at a game station); `''` hides it. */
  setActivity(activity: string): void {
    const icon = activityIcon(activity);
    if (this.activityMarker.text !== icon) this.activityMarker.setText(icon);
  }

  /** Puts the party hat on or takes it off. */
  setPartyHat(on: boolean): void {
    if (on === this.hat.visible) return;
    this.hat.setVisible(on);
    this.label.setY(on ? LABEL_Y - HAT_TAG_RAISE : LABEL_Y);
    this.bubble?.setY(this.label.y - 14);
    this.placeTypingBubble();
    this.syncHat();
  }

  /**
   * Moves to a tile; `durationMs` 0 snaps (used for corrections and teleports). A sprinting step
   * kicks up a puff of dust where it starts and plays the walk faster.
   */
  moveTo(tileX: number, tileY: number, durationMs: number, sprint = false): void {
    if (tileX === this.tileX && tileY === this.tileY) return;
    const dx = tileX - this.tileX;
    const dy = tileY - this.tileY;
    const oneStep = Math.abs(dx) + Math.abs(dy) === 1;
    if (oneStep) this.facing = dx > 0 ? 'right' : dx < 0 ? 'left' : dy > 0 ? 'down' : 'up';
    if (sprint && oneStep && durationMs > 0) {
      const from = Avatar.toPixels(this.tileX, this.tileY);
      puffDust(
        this.scene,
        from.x,
        from.y - DUST_LIFT,
        decorDepth(this.tileY),
        prefersReducedMotion(),
      );
    }
    this.sprinting = sprint;
    this.tileX = tileX;
    this.tileY = tileY;
    this.tween?.stop();
    const target = Avatar.toPixels(tileX, tileY);
    this.idleTimer?.remove();
    if (durationMs <= 0) {
      this.container.setPosition(target.x, target.y);
      this.walking = false;
      this.play('idle');
    } else {
      this.walking = true;
      this.play('walk');
      this.tween = this.scene.tweens.add({
        targets: this.container,
        x: target.x,
        y: target.y,
        duration: durationMs,
        ease: 'Linear',
        onComplete: () => {
          this.idleTimer = this.scene.time.delayedCall(IDLE_AFTER_STEP_MS, () => {
            this.walking = false;
            this.play('idle');
          });
        },
      });
    }
    this.updateDepth();
  }

  /** Shows a speech bubble above the head; a new message replaces the previous one. */
  say(text: string): void {
    this.clearBubble();
    const shown = text.length > BUBBLE_MAX_CHARS ? `${text.slice(0, BUBBLE_MAX_CHARS - 1)}…` : text;
    this.bubble = this.scene.add
      .text(0, this.label.y - 14, shown, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#1f2430',
        backgroundColor: '#f4f1e8',
        padding: { x: 4, y: 2 },
        wordWrap: { width: 128, useAdvancedWrap: true },
        align: 'center',
      })
      .setOrigin(0.5, 1)
      .setResolution(2);
    this.container.add(this.bubble);
    this.placeTypingBubble();
    // Keep the bubble above neighbours' name tags.
    this.updateDepth();
    const duration = Math.min(BUBBLE_MS_MAX, BUBBLE_MS_BASE + shown.length * BUBBLE_MS_PER_CHAR);
    this.bubbleTimer = this.scene.time.delayedCall(duration, () => this.clearBubble());
  }

  /**
   * Shows or hides the "..." of a nearby player typing a message (spec §18). Without a fresh
   * `true` it disappears by itself, so a lost "stopped typing" never leaves it stuck.
   */
  setTyping(on: boolean): void {
    if (!on) {
      this.clearTyping();
      return;
    }
    this.typingExpiry?.remove();
    this.typingExpiry = this.scene.time.delayedCall(TYPING_BUBBLE_TTL_MS, () => this.clearTyping());
    if (this.typingBubble) return;
    const still = prefersReducedMotion();
    const bubble = this.scene.add
      .text(0, 0, still ? '...' : TYPING_FRAMES[0], {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#1f2430',
        backgroundColor: '#f4f1e8',
        padding: { x: 4, y: 0 },
      })
      .setOrigin(0.5, 1)
      .setResolution(2);
    this.typingBubble = bubble;
    this.container.add(bubble);
    this.placeTypingBubble();
    this.updateDepth();
    if (still) return;
    let frame = 0;
    this.typingDots = this.scene.time.addEvent({
      delay: TYPING_FRAME_MS,
      loop: true,
      callback: () => {
        frame = (frame + 1) % TYPING_FRAMES.length;
        bubble.setText(TYPING_FRAMES[frame] ?? '...');
      },
    });
  }

  /**
   * Plays an emote over the head for a few seconds: the animated bubble, or its first frame with
   * reduced motion. A new emote replaces the one showing.
   */
  showEmote(id: EmoteId): void {
    this.clearEmote();
    if (!this.scene.textures.exists(emoteTextureKey(id))) return;
    const sprite = this.scene.add
      .sprite(0, 0, emoteTextureKey(id), 0)
      .setOrigin(0.5, EMOTE_TAIL_ORIGIN);
    if (!prefersReducedMotion() && this.scene.anims.exists(emoteAnimationKey(id)))
      sprite.play(emoteAnimationKey(id));
    this.emoteSprite = sprite;
    this.container.add(sprite);
    this.placeEmote();
    this.updateDepth();
    this.emoteTimer = this.scene.time.delayedCall(EMOTE_DURATION_MS, () => this.clearEmote());
  }

  private clearEmote(): void {
    this.emoteTimer?.remove();
    this.emoteTimer = undefined;
    this.emoteSprite?.destroy();
    this.emoteSprite = undefined;
    this.updateDepth();
  }

  /** Above the name tag, speech bubble and typing dots. */
  private placeEmote(): void {
    if (!this.emoteSprite) return;
    let y = this.label.y - 14;
    if (this.bubble) y = this.bubble.y - this.bubble.displayHeight - 2;
    if (this.typingBubble) y = this.typingBubble.y - this.typingBubble.displayHeight - 2;
    this.emoteSprite.setY(y);
  }

  destroy(): void {
    this.emoteTimer?.remove();
    this.tween?.stop();
    this.idleTimer?.remove();
    this.bubbleTimer?.remove();
    this.typingExpiry?.remove();
    this.typingDots?.remove();
    this.container.destroy(true);
  }

  private clearBubble(): void {
    this.bubbleTimer?.remove();
    this.bubbleTimer = undefined;
    this.bubble?.destroy();
    this.bubble = undefined;
    this.placeTypingBubble();
    this.updateDepth();
  }

  private clearTyping(): void {
    this.typingExpiry?.remove();
    this.typingExpiry = undefined;
    this.typingDots?.remove();
    this.typingDots = undefined;
    this.typingBubble?.destroy();
    this.typingBubble = undefined;
    this.updateDepth();
  }

  /** Over the head, or on top of the speech bubble when one is showing. */
  private placeTypingBubble(): void {
    if (!this.typingBubble) return;
    const y = this.bubble ? this.bubble.y - this.bubble.displayHeight - 2 : this.label.y - 14;
    this.typingBubble.setY(y);
    this.placeEmote();
  }

  /**
   * The appearance's character; the default one before the user chose (an empty key) or if its
   * sheet did not load. Hidden only if no sheet loaded at all.
   */
  private applyLook(): void {
    const wanted = parseAppearanceKey(this.currentKey)?.character ?? DEFAULT_APPEARANCE.character;
    this.character = [wanted, DEFAULT_APPEARANCE.character].find((id) =>
      this.scene.textures.exists(sheetKey(id)),
    );
    this.sprite.setVisible(this.character !== undefined);
    if (!this.character) return;
    this.sprite.setScale(SHEET_SCALE);
    const seatedAnimation =
      this.currentStatus === 'offline'
        ? ensureSeatedAnimation(this.scene, this.character)
        : undefined;
    if (seatedAnimation) this.sprite.play(seatedAnimation, true);
    else this.play(this.walking ? 'walk' : 'idle');
    this.syncHat();
  }

  /**
   * Shows the hat frame that matches the body: the same idle or walk frame of the character's
   * sheet, or the back view lowered onto the seat while typing at the desk.
   */
  private syncHat(): void {
    if (!this.hat.visible || !this.character) return;
    const index = Number(this.sprite.frame.name);
    let animation: CharacterAnimation = 'idle';
    let frame = firstFrame('down');
    let y = -HAT_LIFT * SHEET_SCALE;
    if (this.currentStatus === 'offline') {
      frame = firstFrame('up');
      y += seatedHeadDrop(Number.isInteger(index) ? index : 0) * SHEET_SCALE;
    } else if (Number.isInteger(index)) {
      // Sheet frames are numbered row by row; the row says idle or walk, the column the frame.
      const columns = Math.floor(this.sprite.texture.getSourceImage().width / SHEET_FRAME.width);
      animation = Math.floor(index / columns) === SHEET_ROWS.walk ? 'walk' : 'idle';
      frame = index % columns;
    }
    const hat = partyHatFrame(this.scene, animation, frame);
    if (!hat) return;
    this.hat.setTexture(hat.key, hat.frame).setScale(SHEET_SCALE).setY(y);
  }

  private play(animation: CharacterAnimation): void {
    if (!this.character || this.currentStatus === 'offline') return;
    const key = animationKey(this.character, animation, this.facing);
    this.sprite.anims.timeScale =
      animation === 'walk' && this.sprinting ? SPRINT_SPEED_MULTIPLIER : 1;
    if (this.scene.anims.exists(key)) this.sprite.play(key, true);
  }

  private applyStatus(): void {
    this.applyLook();
    this.label.setAlpha(this.currentStatus === 'offline' ? 0.7 : 1);
    // Offline is taken to mean working at the desk (no "zz"); only "away" gets the marker.
    this.marker.setVisible(this.currentStatus === 'away');
  }

  private updateDepth(): void {
    // Anyone with a bubble draws above neighbours' name tags.
    const speaking =
      this.bubble !== undefined ||
      this.typingBubble !== undefined ||
      this.emoteSprite !== undefined;
    this.container.setDepth(speaking ? speakingDepth(this.tileY) : characterDepth(this.tileY));
  }

  /** Feet at the bottom-centre of the tile. */
  private static toPixels(tileX: number, tileY: number): { x: number; y: number } {
    return { x: tileX * TILE_SIZE + TILE_SIZE / 2, y: (tileY + 1) * TILE_SIZE };
  }
}
